/**
 * @fileoverview API layer for AI Competitor Analysis. All endpoint knowledge for
 * this feature lives here; components call these named functions and never
 * assemble URLs themselves.
 */
import { apiCall } from "../../_services/apiCall";
import config from "../../config/config";

const BASE = `${config.POSTLOGIN_API_URL_COMPANY}competitor-analysis`;

/**
 * Loads the prefill for the Analyze-a-Company step.
 *
 * @returns {Promise<object>} Envelope carrying company name, resolved sector and sector options.
 */
export const getContext = () => apiCall(`${BASE}/context`, {}, {}, "GET");

/**
 * Loads the company's most recent run.
 *
 * @returns {Promise<object>} Envelope whose data is the run view, or null when none exists.
 */
export const getLatestRun = () =>
  apiCall(`${BASE}/runs/latest`, {}, {}, "GET", false, false);

/**
 * Loads one run with its peers and sections.
 *
 * @param {number} runId - Run to load.
 * @returns {Promise<object>} Envelope carrying the run view.
 *
 * @remarks
 * Alerts are suppressed because this is the polling endpoint — a global alert
 * on every tick would bury the page while a run is generating.
 */
export const getRun = (runId) =>
  apiCall(`${BASE}/runs/${runId}`, {}, {}, "GET", false, false);

/**
 * Opens a run and asks the AI for a peer group.
 *
 * @param {{sectorName: string, baseCompanyName: string, industryId: number|null}} body - Setup selections.
 * @returns {Promise<object>} Envelope carrying the run with its recommended peers.
 */
export const createRun = (body) =>
  apiCall(`${BASE}/runs`, {}, body, "POST", false, false);

/**
 * Confirms the peer selection and starts generation.
 *
 * @param {number} runId - Run to analyse.
 * @param {number[]} selectedCompetitorIds - Peers to benchmark against.
 * @returns {Promise<object>} Envelope carrying the run once generation has started.
 */
export const analyzeRun = (runId, selectedCompetitorIds) =>
  apiCall(
    `${BASE}/runs/${runId}/analyze`,
    {},
    { selectedCompetitorIds },
    "POST",
    false,
    false,
  );
